<template>
  <div v-if="isMobile" class="flex flex-col rounded-md border border-neutral-200">
    <UiLink :id="`wishlist-item-name-${item.id}`" :to="`/${item.slug}`" :underline="false">
      <UiImage
        v-if="item.image_url"
        :id="`wishlist-item-${item.id}`"
        :src="item.image_url"
        :alt="item.name"
        class="w-40 rounded-md"
      />
    </UiLink>
  </div>

  <div v-else class="mx-3 mb-4">
    <UiLink :id="`wishlist-item-${item.id}`" :to="`/${item.slug}`" :underline="false" class="flex flex-row">
      <div class="flex w-32 items-start">
        <UiImage
          v-if="item.image_url"
          :id="`wishlist-item-${item.id}`"
          :src="item.image_url"
          :alt="item.name"
          class="rounded-md"
        />
      </div>
      <div class="ml-4 flex w-full flex-col">
        <UiText class="text-black">{{ item.name }}</UiText>
        <UiLabel class="mt-1 text-black">
          {{
            formatPrice(
              locale as Locale,
              getItemPrice(item)?.amount ?? 0,
              getItemPrice(item)?.currency_code ?? Currency.USD,
            )
          }}
        </UiLabel>
      </div>
    </UiLink>
  </div>

  <div class="my-3 flex flex-row justify-between px-2">
    <UiButton
      :id="`wishlist-item-add-to-cart-${item.id}`"
      variant="tertiary"
      class="items-center border-neutral-200 shadow-none hover:!shadow-none focus:!shadow-none"
      :icon="IconCart"
      :loading="isLoading"
      @click="addItemToCart"
    >
      {{ $t(isMobile ? 'wishlist.add' : 'wishlist.addToCart') }}
    </UiButton>

    <UiTooltip :id="`wishlist-item-remove-${item.id}`" :label="$t('removeModal.removeItem')">
      <UiIconButton
        :id="`wishlist-item-remove-${item.id}`"
        :icon="IconTrash"
        icon-class="!fill-primary-500"
        :disabled="isLoading"
        @click="isModalOpen = !isModalOpen"
      />
    </UiTooltip>

    <UiModal
      :id="`wishlist-item-remove-${item.id}`"
      :is-open="isModalOpen"
      :toggle="(value: boolean) => (isModalOpen = value)"
      :close="() => (isModalOpen = false)"
      :title="$t('removeModal.removeItemText')"
      :confirm-label="$t('general.confirm')"
      :cancel-label="$t('general.cancel')"
      :confirm-click="removeItem"
      :cancel-click="() => (isModalOpen = false)"
    >
      <UiText type="p">{{ $t('removeModal.content') }}</UiText>
    </UiModal>
  </div>
</template>

<script lang="ts" setup>
import { IconCart, IconTrash } from '#components';

import type { WishlistItemProps } from '~/components/Wishlist/types';
import type { WishlistItem } from '~/stores/wishlist/types';

import { useAlertStore } from '~/stores/alert/alertStore';
import { useAppStore } from '~/stores/app/appStore';
import { useCartStore } from '~/stores/cart/cartStore';
import { CartActionType } from '~/stores/cart/types';
import { WishlistActionType } from '~/stores/wishlist/types';
import { useWishlistStore } from '~/stores/wishlist/wishlistStore';

const { isMobile } = useBreakpoints();
const { locale } = useI18n();
const alertStore = useAlertStore();

const wishlistStore = useWishlistStore();
const cartStore = useCartStore();
const appStore = useAppStore();

const props = defineProps<WishlistItemProps>();

const isLoading = ref<boolean>(false);
const isModalOpen = ref<boolean>(false);

const getItemPrice = (item: WishlistItem): Price | undefined =>
  item.prices.find((price) => price.currency_code === appStore.getCurrentCurrency);

const removeItem = (): void => {
  isLoading.value = true;

  wishlistStore
    .updateWishlist({
      actions: [
        {
          id: props.item.id,
          type: WishlistActionType.REMOVE_ITEM,
        },
      ],
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .finally(() => (isLoading.value = false));
};

const addItemToCart = (): void => {
  isLoading.value = true;

  if (!wishlistStore.wishlist) {
    isLoading.value = false;
    return;
  }

  cartStore
    .updateCart({
      actions: [
        {
          quantity: 1,
          sku: props.item.sku,
          type: CartActionType.ADD_ITEM,
        },
      ],
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .then(() => {
      alertStore.add({
        text: 'product.addedToCartSuccessMessage',
        title: 'general.successTitle',
        type: 'positive',
      });
      removeItem();
    })
    .finally(() => (isLoading.value = false));
};
</script>
